import PropTypes from 'prop-types';
import AnimatedModalShell from '../common/AnimatedModalShell';
import { useResponsiveLayout } from '../../hooks/ui/useResponsiveLayout';
import { C, S, SORA } from './loginAuthTheme';
import { overlayLayers } from '../../ui/overlayLayers';
import { foundation } from '../../ui/tokens';
import { fg } from '../../ui/theme/semanticTokens';

const SANS = foundation.fontFamily.sans;

/**
 * Default region geometry — the look every existing caller renders. The
 * forced-MFA gate overrides it through the `chrome` prop (see
 * `MfaEnrollmentGateShell`), so a gate spacing tweak can never leak here.
 */
const DEFAULT_CHROME = {
  gutter: 'clamp(18px, 5vw, 24px)',
  headerPadTop: 'clamp(22px, 5vw, 28px)',
  headerPadBottom: `${S.md}px`,
  headerAlign: 'center',
  iconGap: `${S.sm}px`,
  bodyPadBottomWithFooter: 'clamp(14px, 4vw, 16px)',
  footerPad: 'clamp(12px, 3vw, 14px) clamp(18px, 5vw, 24px)',
};

/**
 * LoginAccessCard — reusable premium shell for pre-app security moments
 * (forced MFA enrollment, recovery codes handoff). Mirrors the login card
 * chrome exactly: washed C.bg scrim, bounded white card, SORA header, and
 * pinned header + scroll body + optional pinned footer so tall content
 * (QR / codes) scrolls internally without growing the card.
 *
 * Composes AnimatedModalShell for entrance animation, keyboardAware iOS
 * reframe, and body-scroll-lock — all inherited unchanged.
 */
export default function LoginAccessCard({
  isOpen = true,
  onExitComplete,
  onScrimClick,
  zIndex = overlayLayers.mfaEnrollmentGate,
  icon,
  title,
  subtitle,
  panelMotion = 'scale',
  skipEnter = false,
  footer,
  children,
  ariaLabel,
  headerContent,
  chrome,
}) {
  const { isMobile } = useResponsiveLayout();
  const k = { ...DEFAULT_CHROME, ...(chrome || null) };

  const dialogStyle = {
    width: '100%',
    maxWidth: isMobile ? 'min(420px, 100%)' : 460,
    // BOUNDED + safe-area aware: never grows past the visible viewport or slides
    // under the Dynamic Island / home indicator. 720 cap (was 640) + a tighter
    // 16px safe-area margin (was 24) so the taller enrollment flow (QR + key +
    // 6-digit step) fits without an internal scroll on most phones.
    maxHeight:
      'min(720px, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px))',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: C.white,
    borderRadius: 20,
    border: `1px solid ${C.border}`,
    boxShadow: '0 8px 32px rgba(15,23,42,0.08)',
    boxSizing: 'border-box',
    padding: 0,
  };

  const scrimStyle = {
    // Flat washed login background — matches the sign-in surface exactly so the
    // journey reads as one continuous screen (NOT a dark dim over a blank page).
    background: C.page,
  };

  return (
    <AnimatedModalShell
      isOpen={isOpen}
      onExitComplete={onExitComplete}
      onScrimClick={onScrimClick}
      zIndex={zIndex}
      dialogStyle={dialogStyle}
      scrimStyle={scrimStyle}
      panelMotion={panelMotion}
      skipEnter={skipEnter}
      keyboardAware
      keyboardAlign="end"
      // Pre-sign-in layout stays as it is (KAN-930 Option B is in-app only).
      typingRest={false}
      dialogProps={{
        role: 'dialog',
        'aria-modal': 'true',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      }}
    >
      {/* ── Pinned header ────────────────────────────────────────────────── */}
      <div
        style={{
          flexShrink: 0,
          textAlign: k.headerAlign,
          padding: `${k.headerPadTop} ${k.gutter} ${k.headerPadBottom}`,
        }}
      >
        {icon && (
          <div style={{ marginBottom: k.iconGap, textAlign: 'center' }} data-testid="login-access-card-icon">
            {icon}
          </div>
        )}
        {/* Raw header node (carries its own typography — e.g. the MFA
            stepper's bars/label), rendered instead of the SORA title. */}
        {headerContent || null}
        {!headerContent && title && (
          <div
            style={{
              fontFamily: SORA,
              fontSize: '1.2rem',
              fontWeight: 600,
              color: fg.ink2,
              marginTop: icon ? 0 : 0,
            }}
          >
            {title}
          </div>
        )}
        {subtitle && (
          <div
            style={{
              fontFamily: SANS,
              fontSize: '0.9rem',
              color: C.readable,
              marginTop: S.xs,
              lineHeight: 1.5,
              maxWidth: 340,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            {subtitle}
          </div>
        )}
      </div>

      {/* ── Scroll body ───────────────────────────────────────────────────── */}
      {/* THIS is what makes tall content (QR/codes) scroll instead of growing
          the card past the viewport. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          // Stop the scroll gesture chaining to the locked page behind the
          // scrim at either end (same reasoning as `modalBodyScrollRegion`,
          // which this pre-app shell predates and doesn't import).
          overscrollBehavior: 'contain',
          paddingLeft: k.gutter,
          paddingRight: k.gutter,
          // Breathing room above the footer hairline so the last body action
          // (e.g. "Cancel setup") isn't glued to it. Without this the body's
          // last button sat flush on the divider while "Sign out" floated in a
          // cavernous footer below — cramped above the line, loose below it.
          paddingBottom: footer ? k.bodyPadBottomWithFooter : k.gutter,
        }}
      >
        {children}
      </div>

      {/* ── Pinned footer (optional) ──────────────────────────────────────── */}
      {footer ? (
        <div
          data-testid="login-access-card-footer"
          style={{
            flexShrink: 0,
            borderTop: `1px solid ${C.border}`,
            padding: k.footerPad,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          {footer}
        </div>
      ) : null}
    </AnimatedModalShell>
  );
}

LoginAccessCard.propTypes = {
  isOpen: PropTypes.bool,
  onExitComplete: PropTypes.func,
  /** If omitted, the card is non-dismissible (no scrim-click). */
  onScrimClick: PropTypes.func,
  zIndex: PropTypes.number,
  icon: PropTypes.node,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  panelMotion: PropTypes.oneOf(['scale', 'fade']),
  /** Splash handoff: appear at rest under the opaque splash (no enter beat). */
  skipEnter: PropTypes.bool,
  footer: PropTypes.node,
  children: PropTypes.node,
  ariaLabel: PropTypes.string,
  /** Raw pinned-header node rendered under the icon INSTEAD of `title`. */
  headerContent: PropTypes.node,
  /** Region geometry overrides (gate-only); omitted = the default look. */
  chrome: PropTypes.shape({
    gutter: PropTypes.string,
    headerPadTop: PropTypes.string,
    headerPadBottom: PropTypes.string,
    headerAlign: PropTypes.string,
    iconGap: PropTypes.string,
    bodyPadBottomWithFooter: PropTypes.string,
    footerPad: PropTypes.string,
  }),
};
